import type { ErrorAuthenticatorAssuranceLevelNotSatisfied } from "@ory/kratos-client";
import type { AxiosError } from "axios";
import type { Dispatch, SetStateAction } from "react";
import type { NavigateFunction } from "react-router";

// A small function to help us deal with errors coming from fetching a flow.
export function handleGetFlowError<S>(
	navigate: NavigateFunction,
	// flowType is used solely as a React Router path segment (navigate(`/${flowType}`)).
	// It must match the local route path, not the Kratos flow-type identifier.
	// For example, the verification flow uses the route "/verify" (not "/verification").
	flowType: "login" | "registration" | "settings" | "recovery" | "verify",
	resetFlow: Dispatch<SetStateAction<S | undefined>>,
) {
	return async (
		err: AxiosError<ErrorAuthenticatorAssuranceLevelNotSatisfied>,
	) => {
		switch (err.response?.data.error?.id) {
			case "session_aal2_required":
				// 2FA is enabled and enforced, but user did not perform 2fa yet!
				window.location.href = err.response?.data.redirect_browser_to ?? "/";
				return;
			case "session_already_available":
				// User is already signed in, let's redirect them home!
				navigate("/");
				return;
			case "session_refresh_required":
				// We need to re-authenticate to perform this action
				window.location.href = err.response?.data.redirect_browser_to ?? "/";
				return;
			case "self_service_flow_return_to_forbidden":
				// The flow expired, let's request a new one.
				resetFlow(undefined);
				navigate(`/${flowType}`);
				return;
			case "self_service_flow_expired":
				// The flow expired, let's request a new one.
				resetFlow(undefined);
				navigate(`/${flowType}`);
				return;
			case "security_csrf_violation":
				// A CSRF violation occurred. Best to just refresh the flow!
				resetFlow(undefined);
				navigate(`/${flowType}`);
				return;
			case "security_identity_mismatch":
				// The requested item was intended for someone else. Let's request a new flow...
				resetFlow(undefined);
				navigate(`/${flowType}`);
				return;
			case "browser_location_change_required":
				// Ory Kratos asked us to point the user to this URL.
				window.location.href = err.response.data.redirect_browser_to ?? "/";
				return;
		}

		switch (err.response?.status) {
			case 410:
				// The flow expired, let's request a new one.
				resetFlow(undefined);
				navigate(`/${flowType}`);
				return;
		}

		if (err.response?.data.error?.message) {
			navigate(
				`/error?error_description=${err.response?.data.error.message}&return_to=/${flowType}`,
			);
			return;
		}

		// We are not able to handle the error? Return it.
		return Promise.reject(err);
	};
}

// A small function to help us deal with errors coming from initializing a flow.
export const handleFlowError = handleGetFlowError;
